import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Button,
  IconButton,
  Avatar,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Box } from "@mui/system";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Grid from "@mui/material/Grid2";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { setOriginalParams, setSort } from "../../../../features/mapsSlice";
import { formatDate } from "../../../../utils/helpers";

interface Props {
  mapsData: any[];
}

const MapsTable = ({ mapsData }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tablePage, setTablePage] = React.useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = React.useState(10);
  const [openBrandsDialog, setOpenBrandsDialog] = React.useState(false);
  const [selectedBrands, setSelectedBrands] = React.useState<any[]>([]);
  const [selectedMapName, setSelectedMapName] = React.useState<string>("");
  const interfaceData = useSelector((state: any) => state.maps.interfaceData);
  const [brandSortKey, setBrandSortKey] = React.useState<
    "brand_name" | "brand_id"
  >("brand_name");
  const [brandSortDirection, setBrandSortDirection] = React.useState<
    "asc" | "desc"
  >("asc");

  const [selectedInterfaceName, setSelectedInterfaceName] =
    React.useState<string>("");
  const sort = useSelector((state: any) => state.maps.sort);
  const destinationTablesData = useSelector(
    (state: any) => state.maps.destinationTablesData,
  );

  // Function to get destination table by ID
  const getDestinationTableById = (destinationTableId: string | number) => {
    return (
      destinationTablesData?.find(
        (table: any) => table.id === destinationTableId,
      ) || null
    );
  };

  const handleBrandHeaderClick = (key: "brand_name" | "brand_id") => {
    if (brandSortKey === key) {
      setBrandSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setBrandSortKey(key);
      setBrandSortDirection("asc");
    }
  };
  const sortedBrands = React.useMemo(() => {
    if (!selectedBrands?.length) return [];

    return [...selectedBrands].sort((a, b) => {
      const aVal = a[brandSortKey];
      const bVal = b[brandSortKey];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return brandSortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return brandSortDirection === "asc"
        ? Number(aVal) - Number(bVal)
        : Number(bVal) - Number(aVal);
    });
  }, [selectedBrands, brandSortKey, brandSortDirection]);

  const handleChangePage = (_: any, newPage: number) => {
    setTablePage(newPage);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", newPage.toString()); // 0-indexed
    updatedParams.set("rowsPerPage", tableRowsPerPage.toString());
    setSearchParams(updatedParams);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = +event.target.value;
    setTableRowsPerPage(newRowsPerPage);
    setTablePage(0);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", "0"); // Reset page to 0
    updatedParams.set("rowsPerPage", newRowsPerPage.toString());
    setSearchParams(updatedParams);
  };

  const tableHeaders = [
    { id: 0, KEY: "map_name", label: "Name", sortable: true },
    { id: 1, KEY: "id", label: "ID", sortable: true },
    {
      id: 2,
      KEY: "brands_using_map",
      label: "Brands Using Map",
      sortable: true,
    },
    { id: 3, KEY: "fields_on_map", label: "Fields Connected", sortable: true },
    {
      id: 4,
      KEY: "destination_table_id",
      label: "Destination Table",
      sortable: true,
    },
    { id: 5, KEY: "created_at", label: "Date Created", sortable: true },
    { id: 6, KEY: "created", label: "Created By", sortable: true },
    { id: 7, KEY: "actions", label: "Quick Actions", sortable: false },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Default sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  const sortedRows = getSortedData(mapsData);
  const paginatedMaps = sortedRows.slice(
    tablePage * tableRowsPerPage,
    tablePage * tableRowsPerPage + tableRowsPerPage,
  );

  React.useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "0", 10);
    const rowsParam = parseInt(searchParams.get("rowsPerPage") || "10", 10);

    const validPage = !isNaN(pageParam) && pageParam >= 0 ? pageParam : 0;
    const validRowsPerPage =
      !isNaN(rowsParam) && [10, 25, 100].includes(rowsParam) ? rowsParam : 10;

    setTablePage(validPage);
    setTableRowsPerPage(validRowsPerPage);

    // If URL values were invalid, update them to valid defaults
    if (pageParam !== validPage || rowsParam !== validRowsPerPage) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", validPage.toString());
      updatedParams.set("rowsPerPage", validRowsPerPage.toString());
      setSearchParams(updatedParams, { replace: true });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const totalPages = Math.ceil(mapsData.length / tableRowsPerPage);

    if (tablePage >= totalPages && totalPages > 0) {
      setTablePage(0);

      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", "0");
      setSearchParams(updatedParams, { replace: true });
    }
  }, [mapsData, tablePage, tableRowsPerPage, searchParams, setSearchParams]);

  const handleCreateNewMaps = () => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));
    navigate("/data_collection/unifier_maps/create_new_map");
  };

  const handleEditMaps = (mapId: string) => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));
    navigate(`/data_collection/unifier_maps/edit_map/?mapId=${mapId}`);
  };

  const handleRedirectToMapsInsight = (mapId: string) => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));
    navigate(
      `/data_collection/unifier_maps/insight?mapId=${mapId}&${currentParams}`,
    );
  };

  const handleDuplicateMap = (mapId: string, mapName: string) => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));

    // Encode mapName to make it URL safe
    const encodedName = encodeURIComponent(mapName);

    navigate(
      `/data_collection/unifier_maps/duplicate_map/?mapId=${mapId}&mapName=${encodedName}`,
    );
  };

  const handleCloseBrandsDialog = () => {
    setOpenBrandsDialog(false);

    // 🔁 reset sorting to defaults
    setBrandSortKey("brand_name");
    setBrandSortDirection("asc");
  };

  return (
    <>
      <Paper sx={{ mt: 2 }}>
        <Box sx={{ padding: "8px 16px" }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid size={8}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Map List
              </Typography>
            </Grid>
            <Grid
              size={4}
              container
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                variant="contained"
                color="secondary"
                sx={{ whiteSpace: "nowrap" }}
                onClick={handleCreateNewMaps}
              >
                Create new MAP
                <AddIcon sx={{ width: "20px", ml: "5px" }} />
              </Button>
            </Grid>
          </Grid>
        </Box>
        <TableContainer className="mapsTable" component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header: any) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMaps && paginatedMaps.length > 0 ? (
                paginatedMaps?.map((row: any, index: any) => {
                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        key={index}
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        <TableCell align="left">
                          <Box display="flex" alignItems="center">
                            <span
                              style={{
                                textDecoration: "underline",
                                color: "#9C27B0",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleRedirectToMapsInsight(row?.id)
                              }
                            >
                              {row?.map_name}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell align="left">{row?.id}</TableCell>
                        <TableCell align="left">
                          <Typography
                            sx={{
                              color: "#9C27B0",
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontWeight: 500,
                            }}
                            onClick={() => {
                              setSelectedBrands(row?.brands_details || []);
                              setSelectedMapName(row?.map_name);
                              const matchedInterface = interfaceData?.find(
                                (i: any) => i.id === row?.backend_interface_id,
                              );

                              setSelectedInterfaceName(
                                matchedInterface?.affiliate_system_interface_name ||
                                  "—",
                              );
                              setOpenBrandsDialog(true);
                            }}
                          >
                            {row?.brands_using_map}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">{row?.fields_on_map}</TableCell>
                        <TableCell align="left">
                          {getDestinationTableById(row?.destination_table_id)
                            ?.destination_table || row?.destination_table_id}
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(row?.created)}
                        </TableCell>
                        <TableCell align="left">
                          <>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Avatar
                                sx={{ width: 22, height: 22, fontSize: 12 }}
                              >
                                {row?.created_by?.charAt(0)?.toUpperCase()}
                              </Avatar>
                              {row?.created_by}
                            </div>
                          </>
                        </TableCell>
                        <TableCell align="left">
                          <>
                            <Tooltip title="Edit" placement="top">
                              <IconButton
                                sx={{ width: "40px", height: "40px" }}
                                onClick={() => handleEditMaps(row?.id)}
                              >
                                <EditIcon color="secondary" fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate" placement="top">
                              <IconButton
                                sx={{ width: "40px", height: "40px" }}
                                onClick={() =>
                                  handleDuplicateMap(row?.id, row?.map_name)
                                }
                              >
                                <ContentCopyIcon
                                  fontSize="small"
                                  color="secondary"
                                />
                              </IconButton>
                            </Tooltip>
                          </>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={mapsData?.length}
              rowsPerPage={tableRowsPerPage}
              page={tablePage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                },
              }}
            />
            <Pagination
              count={Math.ceil(mapsData?.length / tableRowsPerPage)}
              page={tablePage + 1}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              onChange={(_, value) => handleChangePage(null, value - 1)}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={mapsData?.length}
              rowsPerPage={tableRowsPerPage}
              page={tablePage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
        </TableContainer>
      </Paper>
      <Dialog
        open={openBrandsDialog}
        onClose={handleCloseBrandsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "40vh",
            overflow: "auto",
          },
        }}
      >
        <DialogTitle sx={{ ml: 2 }}>{selectedMapName}</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2, ml: 2, color: "text.secondary" }}>
            {selectedInterfaceName}
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={brandSortKey === "brand_name"}
                    direction={
                      brandSortKey === "brand_name" ? brandSortDirection : "asc"
                    }
                    onClick={() => handleBrandHeaderClick("brand_name")}
                  >
                    Brand Name
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={brandSortKey === "brand_id"}
                    direction={
                      brandSortKey === "brand_id" ? brandSortDirection : "asc"
                    }
                    onClick={() => handleBrandHeaderClick("brand_id")}
                  >
                    Brand ID
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedBrands.length > 0 ? (
                sortedBrands.map((brand: any) => (
                  <TableRow key={brand.brand_id}>
                    <TableCell>{brand.brand_name}</TableCell>
                    <TableCell>{brand.brand_id}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No brands found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCloseBrandsDialog}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapsTable;
